import React, { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Formik } from 'formik';
import * as Sentry from '@sentry/browser';
import * as yup from 'yup';
import LoginCarousel from '../common/LoginCarousel/loginCarouselComponent';
import { usernameCharacterValidationFn } from '../../utils/textFieldUtil';
import PasswordInput from '../../common/PasswordInput';
import './login.scss';
import { FormatUtil } from 'helper-util';
import {
  amplifyCompleteNewPassword,
  amplifyGetAuthenticatedUser,
  amplifySignIn,
  initializeAmplify,
} from '../../utils/amplify';
import {
  clearAuthToken,
  getUserDetails,
  setAuthToken,
  setPermissions,
  setUserInStorage,
} from '../../services/authService';
import { parseQuery } from '../../utils/queryParse';
import { urlSafeDecode } from '@aws-amplify/core';
import {
  getStringFromStorage,
  removeItemFromStorage,
  setObjectInStorage,
  setStringInLocalStorage,
  setStringInStorage,
} from '../../services/storageService';
import { setClientConfigToStorage } from './loginHelper';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { addLoginDetailLogs, getCognitoClientID, getTenantConfig } from './loginActions';
import FsModalBox from '../../common/fsModalBox';
import { maskLoginErrorMessages, setAuthExpiryTimeFn } from '../../app/appHelper';
import { WarningIcon } from '../../styles/svg/svgIcons';
import { authLogout } from '../../app/appActions';
import { setUser, toggleForceLogout } from './loginSlice';
import { setDarkTheme, setIsLoading } from '../../app/appSlice';
import { isResponseSuccess } from '../../utils/apiResponseUtil';
import { jwtDecode } from 'jwt-decode';
import { setClientConfiguration } from '../../services/clientConfigurationService';
import { useNavigate } from 'react-router-dom';
import NewPassword from './NewPasswordComponent';
import ForcedLogoutToast from './ForcedLogoutToast/ForcedLogoutToastComponent';
import FsLoader from '../../common/fsLoader/fsLoaderComponent';
import { loginToForgetPassword } from '../forgotPassword/forgotPasswordConstant';
import { paths } from '../../routes/routesConfig';

const LoginComponent = () => {
  const [newPassword, setNewPassword] = useState(false);
  const [data, setData] = useState({});
  const [error, setError]: any = useState({});
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(Boolean(localStorage.getItem('rememberMe')));
  const [hideLoginForm, setHideLoginForm] = useState(true);
  const [executeFederation, setExecuteFederation] = useState(
    Boolean(parseQuery(window.location.search).state),
  );
  const [showOnlyLoginCarousel, setShowOnlyLoginCarousel] = useState(true);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const forcedLogout = useSelector((state: RootState) => state.login.forcedLogout);

  useEffect(() => {
    const carouselOnly = hideLoginForm || executeFederation;
    setShowOnlyLoginCarousel(carouselOnly);
  }, [hideLoginForm, executeFederation]);

  useEffect(() => {
    if (loading || showOnlyLoginCarousel) {
      dispatch(setIsLoading(true));
    } else {
      dispatch(setIsLoading(false));
    }
  }, [loading, showOnlyLoginCarousel]);

  useEffect(() => {
    clearAuthToken();
    try {
      const query: any = parseQuery(window.location.search);
      let tenantCode;
      let executeFedetationSignIn = false;
      if (query.state) {
        const isCustomStateIncluded = /-/.test(query.state);
        if (isCustomStateIncluded) {
          const customState: string = query.state.split('-').splice(1).join('-');
          tenantCode = urlSafeDecode(customState);
          setStringInStorage('fedetation', 'fedetation');
        }
      } else if (query.tenant_code) {
        tenantCode = query.tenant_code;
        executeFedetationSignIn = true;
      }

      if (tenantCode) {
        getCognitoClientID(tenantCode).then((webclientReponse: any) => {
          if (webclientReponse) {
            setObjectInStorage('amplifyConfig', webclientReponse);
            setStringInStorage('tenant', tenantCode);
            setClientConfigToStorage(webclientReponse);
            setExecuteFederation(!!webclientReponse?.federation);
            setHideLoginForm(false);
            initializeAmplify(webclientReponse, tenantCode, executeFedetationSignIn).then(
              (result) => {
                if (!result) {
                  amplifyGetAuthenticatedUser().then((res) => {
                    const idToken = res.signInUserSession.idToken.jwtToken;
                    handleLoginSuccess({ idToken }, { email: res.username });
                  });
                }
              },
            );
          } else {
            setError(webclientReponse || '');
            setLoading(false);
          }
        });
      } else {
        throw new Error('Tenant code required');
      }
    } catch (error) {}
  }, [dispatch]);

  const validationSchema = yup.object().shape({
    email: yup.string().required('Username is Required').nullable(),
    password: yup.string().required('Password is Required'),
  });

  const removeAtribute = (id: string) => {
    const fieldEl = document.getElementById(id);
    if (fieldEl) fieldEl.removeAttribute('readOnly');
  };

  const handleLogin = (values: any) => {
    setLoading(true);
    const { email, password } = values;
    amplifySignIn(FormatUtil.text.toLowerCase(email && email.trim()), password && password.trim())
      .then((result) => {
        if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setNewPassword(true);
          setData(result);
        } else if (result.challengeName === 'CUSTOM_CHALLENGE') {
          setData(result);
        } else {
          const idToken =
            result && result.signInUserSession && result.signInUserSession.idToken.jwtToken;
          handleLoginSuccess({ idToken }, values);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError({ ...err, mode: 'Login' });
        setLoading(false);
      });
  };

  function handleLoginSuccess(result, values) {
    const token = result.idToken;
    const user = getUserDetails(token);
    Sentry.setUser({ username: user.email });
    dispatch(setUser(user));
    setUserInStorage(user);
    setAuthToken(token);
    setAuthExpiryTimeFn(history, token, authLogout, navigate);
    setPermissions();
    setStringInLocalStorage('rememberMe', rememberMe ? values.email : '');

    setDarkTheme({ isDark: localStorage.getItem('theme') === 'dark' });

    dispatch(getTenantConfig()).then((result: any) => {
      if (isResponseSuccess(result)) setClientConfigToStorage(result.response.data);

      const decodedToken: any = token && jwtDecode(token);
      setClientConfiguration({
        baseCurrency: decodedToken.baseCurrency,
        timeZone: decodedToken.timeZone,
      });

      const roles = decodedToken && decodedToken['roles'];
      successfulRoute(roles ? JSON.parse(roles) : []);
      dispatch(addLoginDetailLogs());
    });
  }

  function successfulRoute(permissionList) {
    const redirectTo = getStringFromStorage('redirect_to');

    removeItemFromStorage('redirect_to');
    setStringInStorage('tenantPageAccessible', 'false');
    if (redirectTo) {
      navigate(redirectTo);
    } else {
      navigate('/dashboard');
    }
  }

  function handleNewPassword(values) {
    setLoading(true);
    const trimmedPassword = values.password && values.password.trim();
    amplifyCompleteNewPassword(data, trimmedPassword)
      .then((result) => {
        const idToken = result.signInUserSession && result.signInUserSession.idToken.jwtToken;
        handleLoginSuccess({ idToken }, { email: result.username });
        setLoading(false);
      })
      .catch((err) => {
        setError({ ...err, mode: 'NewPassword' });
        setLoading(false);
      });
  }

  return (
    <>
      <div id="outer">
        {forcedLogout && <ForcedLogoutToast onClose={() => dispatch(toggleForceLogout())} />}
        {(showOnlyLoginCarousel || loading) && <FsLoader />}
        <div id="login-container" className="shadow-sm card-login">
          <div className={`left ${showOnlyLoginCarousel && 'left-w-100'}`}>
            <LoginCarousel />
          </div>
          {showOnlyLoginCarousel ? null : (
            <div className="right">
              <span className="f-24 heading d-block font-weight-medium border-bottom pb-20 mb-20 text-left">
                Login to your Account
              </span>
              {newPassword ? (
                <NewPassword handleNewPassword={handleNewPassword} loading={loading} />
              ) : (
                <Formik
                  validationSchema={validationSchema}
                  initialValues={{
                    email: localStorage.getItem('rememberMe'),
                    password: '',
                  }}
                  onSubmit={handleLogin}
                >
                  {({
                    values,
                    errors,
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    touched,
                  }) => (
                    <form onSubmit={handleSubmit} autoComplete="off">
                      <div className={`${!errors.email ? 'form-group' : ''} pb-2`}>
                        <label className="grey-10 mb-1">Username</label>
                        <input
                          id="email"
                          type="text"
                          className="form-control px-3"
                          placeholder="Enter your username here"
                          autoComplete="off"
                          value={values.email || ''}
                          readOnly={true}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          onKeyDown={usernameCharacterValidationFn}
                          onFocus={() => removeAtribute('email')}
                        />
                        {errors.email && touched.email && (
                          <div className="text-danger mt-2 ml-2">{String(errors.email)}</div>
                        )}
                      </div>
                      <div className={`${!errors.password ? 'form-group' : ''} pb-2`}>
                        <label className="grey-10 mb-1">Password</label>
                        <PasswordInput
                          id="password"
                          type="password"
                          className="form-control"
                          placeholder="Enter your password here"
                          autoComplete="off"
                          value={values.password}
                          readOnly={true}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onFocus={() => {
                            removeAtribute('password');
                          }}
                        />
                        {errors.password && touched.password && (
                          <div className="text-danger mt-2 ml-2">{String(errors.password)}</div>
                        )}
                      </div>
                      <Form.Check
                        className="f-14 font-weight-medium mb-2"
                        id={'all'}
                        label="Remember Username"
                        defaultChecked={rememberMe}
                        onChange={() => setRememberMe((prev) => !prev)}
                        type="checkbox"
                      />
                      <button
                        disabled={loading}
                        id="default_FsContainedButton"
                        type="submit"
                        className="btn btn-secondary btn-xl d-block w-100 mt-4 mb-4"
                      >
                        Login
                      </button>
                      <div
                        onClick={() => {
                          setStringInStorage(loginToForgetPassword, 'true');
                          navigate(paths.forgotPassword);
                        }}
                        className="link f-14 d-block text-center mb-3 mt-4 f-16"
                      >
                        Forgot Password
                      </div>
                    </form>
                  )}
                </Formik>
              )}
            </div>
          )}
        </div>
      </div>
      <FsModalBox
        show={error.code}
        headerIcon={
          <span className="d-block m-auto text-danger">
            <WarningIcon />
          </span>
        }
        bodyTitle={''}
        bodyMessage={maskLoginErrorMessages(error)}
        handleModalCancel={() => setError({})}
      />
    </>
  );
};

export default LoginComponent;
